
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { translate } from 'react-i18next'
import { isArray, isString } from 'lodash'
import { withStyles } from '@material-ui/core/styles'
import AppBar from '@material-ui/core/AppBar'
import ToolBar from '@material-ui/core/Toolbar'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grid from '@material-ui/core/Grid'
import MenuIcon from '@material-ui/icons/Menu'
import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'
import Button from '@material-ui/core/Button'

import * as actions from '../../actionTypes'
import MenuActionModal from './MenuActionModal'
import LanguageSelect from '../language'
import { isSaveRequired } from '../../sagas/counseling-session'
import { getPathnameEnding } from '../../utils'

const styles = theme => ({
  appBar: {marginBottom: theme.spacing.unit * 2},
  menuIcon: {color: theme.palette.primary.contrastText},
  menuItem: {height: '100%', width: '100%'},
  select: {
    color: theme.palette.primary.contrastText,
  },
  selectUnderline: {
    '&:after': {
      borderBottom: `1px solid ${theme.palette.primary.contrastText}`
    },
    '&:before': {
      transition: 'none',
      borderBottom: `1px solid ${theme.palette.primary.contrastText}`
    }
  },
  selectDisabled: {
    color: theme.palette.text.primary,
  },
  selectUnderlineDisabled: {
    borderBottom: `1px solid ${theme.palette.text.primary}`,
    '&:before': {
      transition: 'none',
      borderBottom: `1px solid ${theme.palette.text.primary}`
    },
    '&:after': {
      borderBottom: `1px solid ${theme.palette.text.primary}`
    }
  },
  logo: {height: 'auto', maxWidth: '150px'},
  title: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  titleContainer: {
    display: 'flex'
  }
})

// Create `Wire` component to avoid warnings in console
// See issue below
// https://github.com/mui-org/material-ui/issues/12468
const Wire = ({ children, ...props }) => children(props)

export class DcpMenu extends Component {
  state = {
    anchorEl: null,
    isLogoutConfirmationModalVisible: false,
    isSessionConfirmationModalVisible: false
  }

  handleClick(event) {
    this.setState({anchorEl: event.currentTarget})
  }

  handleClose(e) {
      if (
        e.target.className
        && (isArray(e.target.className) || isString(e.target.className))
        && e.target.className.includes('MuiSelect')
      ) {
        return
      }

    this.setState({anchorEl: null})
  }

  handleExitSession(save) {
    this.hideConfirmationModal()
    if (!save) {
      this.props.exitSession()
    } else {
      const screen = getPathnameEnding(this.props.location.pathname)
      this.props.saveAndStop(screen, this.props.sessionId)
    }
  }

  handleLogout = (save) => {
    this.hideConfirmationModal()
    if (!save) {
      this.props.logout()
    } else {
      const screen = getPathnameEnding(this.props.location.pathname)
      this.props.saveAndLogout(screen, this.props.sessionId)
    }
  }

  hideConfirmationModal() {
    this.setState({isLogoutConfirmationModalVisible: false, isSessionConfirmationModalVisible: false})
  }

  isPageWithHeader() {
    return this.props.location.pathname !== '/login'
  }

  isPageWithMenu() {
    return this.isPageWithHeader() && this.props.location.pathname !== '/notice/401'
  }

  render() {
    const {classes, isAuthorized, t, logo, sessionId, languageSelectDisabled} = this.props
    const {anchorEl} = this.state
    const showMenu = isAuthorized && this.isPageWithMenu()
    const showHeader = showMenu || this.isPageWithHeader()
    return (
      showHeader && (
        <React.Fragment>
          <ClickAwayListener
            // only want listener active when menu is visible
            mouseEvent={anchorEl ? 'onMouseUp' : false}
            onClickAway={this.handleClose.bind(this)}
          >
            <AppBar
              className={classes.appBar}
              position="static"
            >
              <ToolBar>
                <Grid container>
                  <Grid item container spacing={16} alignItems="center">
                    {isAuthorized && (
                      <Grid item xs="auto">
                        <img className={classes.logo} src={logo.srcURL} alt={logo.altText}/>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid container alignItems="center" justify="center">
                  <Grid item className={classes.titleContainer} xs="auto">
                    <Typography
                      className={classes.title}
                      color="secondary"
                      variant="title"
                    >
                      {t('Decision Counseling Program')}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container justify="flex-end">
                    {showMenu && (
                      <Grid item xs="auto">
                        <Hidden xsDown={true}>
                          <LanguageSelect
                            classes={{
                              root: classes.select,
                              icon: languageSelectDisabled ? classes.selectDisabled : classes.select,
                              disabled: `${classes.selectUnderlineDisabled} ${classes.selectDisabled}`
                            }}
                            disabled={languageSelectDisabled}
                            input={
                              <Input
                                classes={{
                                  root: classes.selectUnderline,
                                  input: classes.selectUnderline,
                                  underline: classes.selectUnderline,
                                  focused: classes.selectUnderline,
                                  disabled: classes.selectUnderlineDisabled
                                }}
                              />
                            }
                          />
                        </Hidden>
                        <IconButton
                          aria-label="Menu"
                          className={classes.menuIcon}
                          color="secondary"
                          onClick={this.handleClick.bind(this)}
                        >
                          <MenuIcon/>
                      </IconButton>
                      </Grid>
                  )}
                </Grid>
              </ToolBar>
              <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
              >
                {sessionId && (
                  <MenuItem className={classes.menuItem} onClick={() => this.showConfirmationModal()}>
                    {t('Stop Session')}
                  </MenuItem>
                )}

                <MenuItem className={classes.menuItem} onClick={() => this.showConfirmationModal(true)}>
                  {t('Log Out')}
                </MenuItem>
                <Wire>
                  {props => (
                    <Hidden smUp={true}>
                      <MenuItem>
                        <LanguageSelect
                          classes={{
                            root: classes.select,
                            icon: languageSelectDisabled ? classes.selectDisabled : classes.select,
                            disabled: `${classes.selectUnderlineDisabled} ${classes.selectDisabled}`
                          }}
                          disabled={languageSelectDisabled}
                          input={
                            <Input
                              classes={{
                                root: classes.selectUnderline,
                                input: classes.selectUnderline,
                                underline: classes.selectUnderline,
                                focused: classes.selectUnderline,
                                disabled: classes.selectUnderlineDisabled
                              }}
                            />
                          }
                        />
                      </MenuItem>
                    </Hidden>
                  )}
                </Wire>
              </Menu>
            </AppBar>
          </ClickAwayListener>
          {this.renderConfirmationModal()}
        </React.Fragment>
      )
    )
  }

  renderConfirmationModal = () => {
    const {isLogoutConfirmationModalVisible, isSessionConfirmationModalVisible} = this.state
    const { currentScreen, counselingSession, t } = this.props

    const open = (isLogoutConfirmationModalVisible || isSessionConfirmationModalVisible)
    const canSave = isSaveRequired(currentScreen, counselingSession)
    let buttons = []
    let heading = ''

    if (open && isLogoutConfirmationModalVisible) {
      heading = 'Log Out Confirm'
      buttons = [
        <Button color="primary" onClick={() => this.handleLogout()}>{t('Log Out')}</Button>
      ]

      if (canSave) {
        buttons.unshift(<Button color="primary" onClick={() => this.handleLogout(true)}>{t('Logout And Save')}</Button>)
      }

    } else {
      heading = 'Stop Session Confirm'
      buttons = [
        <Button color="primary" onClick={() => this.handleExitSession()}>{t('Stop Session')}</Button>
      ]

      if (canSave) {
        buttons.unshift(<Button color="primary" onClick={() => this.handleExitSession(true)}>{t('Stop Session And Save')}</Button>)
      }
    }

    return (
      <MenuActionModal
        open={open}
        onCancel={() => this.hideConfirmationModal()}
        heading={heading}
        buttons={buttons}
      />
    )
  }

  showConfirmationModal(logout) {
    const update = logout ? {isLogoutConfirmationModalVisible: true} : {isSessionConfirmationModalVisible: true}

    this.setState(update)
  }
}

const mapDispatchToProps = dispatch => ({
  exitSession() {
    const action = {type: actions.SESSION_EXIT_REQUESTED} // clears session data
    dispatch(action)
  },
  logout() {
    const action = {type: actions.LOGOUT_REQUESTED}
    dispatch(action)
  },
  saveAndLogout(currentScreen, sessionId) {
    dispatch({
      type: actions.COUNSELING_SESSION_NAVIGATION,
      payload: {
        lastScreenSaved: currentScreen,
        sessionId: sessionId,
        shouldGotoNextPage: 0
      }
    })
  },
  saveAndStop(currentScreen, sessionId) {
    dispatch({
      type: actions.COUNSELING_SESSION_NAVIGATION,
      payload: {
        lastScreenSaved: currentScreen,
        sessionId: sessionId,
        pathname: '/',
        shouldGotoNextPage: 0
      }
    })
  }
})

const mapStateToProps = state => ({
  languageSelectDisabled: !!state.counselingSession.sessionId,
  isAuthorized: state.authorization.isAuthorized,
  logo: state.globalSettings.logo,
  sessionId: state.counselingSession.sessionId,
  currentScreen: state.counselingSession.counselingSessionData.lastScreenSaved,
  counselingSession: state.counselingSession
})

DcpMenu.propTypes = {
  isAuthorized: PropTypes.bool
}

export default compose(
  translate(),
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(DcpMenu)



// WEBPACK FOOTER //
// ./src/components/menu/Menu.js